// BOORO: what an approval will actually do. The person sees the mail they are
// about to send, not a JSON dump of the tool call.
import {
  connectorKindFromToolName,
  EMAIL_CONNECTOR_SLUGS,
  PURCHASE_CONNECTOR_SLUGS,
} from "@rakazo/core";

export type ApprovalPreview =
  | { kind: "email"; to: string; cc: string | null; subject: string; body: string }
  | { kind: "calendar"; title: string; start: string; end: string; attendees: string[] }
  | { kind: "payment"; amount: string; currency: string; recipient: string }
  | { kind: "page"; title: string; content: string; path: string | null; office: string | null }
  | { kind: "generic"; fields: { label: string; value: string }[] };

const MAX_PREVIEW_VALUE = 2_000;

/** The field labels a person expects, in the order they read them. */
const GENERIC_LABELS: Record<string, string> = {
  to: "To",
  cc: "Cc",
  bcc: "Bcc",
  subject: "Subject",
  body: "Text",
  title: "Title",
  start: "Start",
  end: "End",
  amount: "Amount",
  currency: "Currency",
  recipient: "Recipient",
  collection: "Collection",
  name: "Name",
  text: "Text",
  html: "Text",
  content: "Text",
  message: "Text",
  value: "Value",
};

function text(args: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = args[key];
    if (value == null || value === "") continue;
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
  }
  return "";
}

function list(args: Record<string, unknown>, ...keys: string[]): string[] {
  for (const key of keys) {
    const value = args[key];
    if (Array.isArray(value)) {
      const items = value.filter((item): item is string => typeof item === "string" && item !== "");
      if (items.length > 0) return items;
    }
    if (typeof value === "string" && value.trim() !== "") {
      return value
        .split(/[,;]/)
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }
  return [];
}

/** The connector a tool belongs to, so a mail tool is recognized as one. */
function connectorKind(toolName: string, connectorKinds: readonly string[]): string {
  return connectorKindFromToolName(toolName, [...connectorKinds]);
}

function isEmailTool(toolName: string, kind: string): boolean {
  if (EMAIL_CONNECTOR_SLUGS.has(kind.toLowerCase())) return true;
  return /send.*mail|gmail_send|outlook_send|mail_send|send_email/i.test(toolName);
}

function isPaymentTool(toolName: string, kind: string): boolean {
  if (PURCHASE_CONNECTOR_SLUGS.has(kind.toLowerCase())) return true;
  return /purchase|pay_|charge|checkout|buy_/i.test(toolName);
}

/** The tool that proposes a page of the handbook. */
function isPageTool(toolName: string): boolean {
  return toolName === "handbuch_schreiben";
}

function isCalendarTool(toolName: string, kind: string): boolean {
  if (["gcal", "google_calendar", "outlook_calendar", "caldav"].includes(kind.toLowerCase())) {
    return true;
  }
  return /calendar|event_create|create_event/i.test(toolName);
}

function genericFields(args: Record<string, unknown>): { label: string; value: string }[] {
  const fields: { label: string; value: string }[] = [];
  // The known fields come first, in the order a person reads them; the rest
  // keep the order of the request, so the list does not jump around.
  const keys = [
    ...Object.keys(GENERIC_LABELS).filter((key) => key in args),
    ...Object.keys(args).filter((key) => !(key.toLowerCase() in GENERIC_LABELS)),
  ];
  for (const key of keys) {
    const value = args[key];
    if (value == null || value === "") continue;
    if (typeof value === "object") continue;
    const label = GENERIC_LABELS[key.toLowerCase()] ?? key.replace(/_/g, " ");
    const shown = typeof value === "string" ? value : String(value);
    fields.push({ label, value: shown.slice(0, MAX_PREVIEW_VALUE) });
  }
  return fields.slice(0, 12);
}

/**
 * What the person is about to approve. An unknown action falls back to its
 * arguments as fields, so the card never shows a raw JSON blob.
 */
export function approvalPreview(
  toolName: string,
  args: Record<string, unknown>,
  options: { connectorKinds?: readonly string[] } = {},
): ApprovalPreview {
  const kind = connectorKind(toolName, options.connectorKinds ?? []);
  if (isPageTool(toolName)) {
    // The whole page: a person approves what agents will follow, not a cut.
    return {
      kind: "page",
      title: text(args, "title"),
      content: text(args, "content"),
      path: text(args, "pfad") || null,
      office: text(args, "office") || null,
    };
  }
  if (isEmailTool(toolName, kind)) {
    return {
      kind: "email",
      to: text(args, "to", "recipient", "recipients", "email"),
      cc: text(args, "cc", "carbon_copy") || null,
      subject: text(args, "subject", "title"),
      body: text(args, "body", "text", "html", "content", "message"),
    };
  }
  if (isCalendarTool(toolName, kind)) {
    return {
      kind: "calendar",
      title: text(args, "title", "summary", "subject"),
      start: text(args, "start", "start_time", "starts_at", "begin"),
      end: text(args, "end", "end_time", "ends_at"),
      attendees: list(args, "attendees", "guests", "participants"),
    };
  }
  if (isPaymentTool(toolName, kind)) {
    return {
      kind: "payment",
      amount: text(args, "amount", "total", "value", "price"),
      currency: text(args, "currency", "currency_code"),
      recipient: text(args, "recipient", "to", "payee", "merchant"),
    };
  }
  return { kind: "generic", fields: genericFields(args) };
}

/** What kind of action a tool is, so a rule about it reads as an action, not a tool name. */
export function approvalToolKind(toolName: string): ApprovalPreview["kind"] {
  const kind = connectorKind(toolName, []);
  if (isPageTool(toolName)) return "page";
  if (isEmailTool(toolName, kind)) return "email";
  if (isCalendarTool(toolName, kind)) return "calendar";
  if (isPaymentTool(toolName, kind)) return "payment";
  return "generic";
}

/** A tool name as words: "gmail.send_email" becomes "gmail send email". */
export function readableToolName(toolName: string): string {
  return toolName.replace(/[._:-]+/g, " ").trim();
}

/** The fields a person may change before approving. Everything else is fixed. */
export function editablePreviewFields(preview: ApprovalPreview): string[] {
  if (preview.kind === "email") return ["to", "cc", "subject", "body"];
  if (preview.kind === "calendar") return ["title", "start", "end", "attendees"];
  if (preview.kind === "payment") return ["amount", "currency", "recipient"];
  if (preview.kind === "page") return ["title", "content"];
  return preview.fields.map((field) => field.label);
}

/**
 * The changed arguments, or the reason they may not be changed. Only the
 * fields the preview names may differ; a new receiver or tool never slips in.
 */
export function applyPreviewChanges(
  original: Record<string, unknown>,
  preview: ApprovalPreview,
  changes: Record<string, string>,
): { ok: true; args: Record<string, unknown> } | { ok: false; error: string } {
  const allowed = new Set(editablePreviewFields(preview));
  const unknown = Object.keys(changes).filter((key) => !allowed.has(key));
  if (unknown.length > 0) {
    return { ok: false, error: `These fields cannot be changed: ${unknown.join(", ")}` };
  }
  const args = { ...original };
  for (const [key, value] of Object.entries(changes)) {
    if (value === "") {
      delete args[key];
      continue;
    }
    args[key] = value;
  }
  return { ok: true, args };
}
